import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import Box from "@mui/material/Box/Box";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import CoreButtonIcon from "../../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import CoreSelectObjArr from "../../../../app/core/components/CoreSelect/CoreSelectObjArr";
import CoreSpinner from "../../../../app/core/components/CoreSpinner/CoreSpinner";
import { useCreateUserMutation } from "../../../api/admin/gestisciUtentiApi";
import CreateOperator from "./CreateOperator";
import CreateUsers from "./CreateUsers";
import CoreSelect from "../../../../app/core/components/CoreSelect/CoreSelect";
import { useGetStudioIdQuery } from "../../../api/admin/gestisciStudioApi";
import { useSelector } from "react-redux";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import { useCreateCommercialistaMutation } from "../../../api/admin/gestisciCommercialistiApi";
import { useGetStudiosQuery } from "../../../api/admin/gestisciStudioApi";
import { TABLE_ROWS_NUMBER } from "../../../../utils/constants/tables";
import StudioFilter from "../../../../app/core/components/UserFilter/StudioFilter";
import { useTranslation } from "react-i18next";
import CoreButtonHoverIcon from "../../../../app/core/components/CoreButtonIcon/CoreButtonHoverIcon";
import { ROLE, SUB_ROLE } from "../../../../utils/constants/profiles";

const ExcelFileUpload = ({ isOperator }) => {
  const { t } = useTranslation();
  const colorPrimary = useSelector((state) => state.app.primaryColor);
  const role = useSelector((state) => state.app.role);
  const subRole = useSelector((state) => state.app.subRole);
  const studioId = useSelector((state) => state.app.studioId);
  const [studio, setStudio] = useState(
    role === ROLE.ADMIN ? "" : { id: studioId }
  );
  const [debouncedInputStudioValue, setDebouncedInputStudioValue] =
    useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [uploadData, setUploadData] = useState({
    data: [],
    previewData: [],
    fileError: null,
    isUploaded: false,
  });

  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [multiUploadInProgress, setMultiUploadInProgress] = useState(false);
  const [selectedRuole, setSelectedRuole] = useState(
    isOperator ? t("Operator") : t("User")
  );
  const [selectedCommercialista, setSelectedCommercialista] = useState();
  const [isAdmin, setIsAdmin] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const ruoli = [t("User"), t("Operator")];

  const [createUser] = useCreateUserMutation();
  const [createCommercialista] = useCreateCommercialistaMutation();

  const { data: studios } = useGetStudiosQuery({
    ...{ currentPage: 1, startRow: 0, endRow: TABLE_ROWS_NUMBER },
    value: debouncedInputStudioValue,
  });

  const {
    data: datas,
    error,
    isLoading,
    isFetching,
  } = useGetStudioIdQuery(studio?.id, { skip: !studio });

  const commercialistiOptions = datas?.Data?.admins?.map((user) => ({
    label: `${user?.name} ${user.surname}`,
    value: user?.id,
  }));

  const onChangeCommercialista = (e) => {
    const { name, value } = e.target;
    setSelectedCommercialista(value);

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];

    if (!file) {
      toast.error(t("InvalidFile"));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        setUploadData({
          data: excelData,
          previewData: excelData,
          fileError: null,
          isUploaded: true,
        });
      } catch (error) {
        console.error(error);
        setUploadData({
          fileError: "Errore durante la lettura del file",
        });
        toast.error(t("InvalidFile"));
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleReset = () => {
    setUploadData({
      data: [],
      previewData: [],
      fileError: null,
      isUploaded: false,
    });
    setUploadSuccess(false);
  };

  const handleUpload = async () => {
    setValidationErrors({});
    const errors = {};

    if (!selectedCommercialista) {
      errors.selectedCommercialista = `${t("SelectOperator")}`;
    }

    if (role === ROLE.ADMIN && !studio) {
      errors.studio = t("FieldRequired");
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (uploadData.data.length === 0) {
      toast.error("No data");
      return;
    }

    setMultiUploadInProgress(true);
    const id = toast.loading(t("Creation..."), {
      type: "info",
    });
    const promises = [];
    for (let i = 1; i < uploadData.data.length; i++) {
      const rowData = uploadData.data[i];
      const userDataBody = {
        name: rowData[0],
        surname: rowData[1],
        email: rowData[2],
        fiscalCode: rowData[3],
        phoneNumber: `${rowData[4] || ""}`,
        studioId: studio?.id,
        adminId: selectedCommercialista,
      };
      if (sendEmail) userDataBody.skipEmailSending = sendEmail;
      rowData[2] && promises.push(await createUser(userDataBody));
    }
    await Promise.all(promises)
      .then((responses) => {
        let success = true;
        for (const res of responses) {
          if (res.data.Result !== 1) {
            toast.update(id, {
              render: t("UsersNotCreated"),
              type: "error",
              autoClose: 1500,
              isLoading: false,
            });
            success = false;
            break;
          }
        }
        if (success) {
          toast.update(id, {
            render: t("UsersCreated"),
            type: "success",
            autoClose: 1500,
            isLoading: false,
          });
        }
      })
      .catch(() => {
        toast.update(id, {
          render: t("UsersNotCreated"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      })
      .finally(() => {
        setMultiUploadInProgress(false);
        // setUploading(false);
        setUploadData({
          isUploaded: false, // Aggiunto qui per ripristinare il Dropzone
        });
      });
  };

  const handleUploadOperator = async () => {
    setValidationErrors({});
    const errors = {};

    if (role === ROLE.ADMIN && !studio) {
      errors.studio = t("FieldRequired");
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (uploadData.data.length === 0) {
      toast.error("No data");
      return;
    }
    setMultiUploadInProgress(true);
    const id = toast.loading(t("Creation..."), {
      type: "info",
    });
    const promises = [];

    for (let i = 1; i < uploadData.data.length; i++) {
      const rowData = uploadData.data[i];
      const operatorDataBody = {
        Name: rowData[0],
        Surname: rowData[1],
        Email: rowData[2],
        StudioId: studio?.id,
        SubRole: isAdmin ? "Responsabile" : "Consulente",
      };
      rowData[2] && promises.push(await createCommercialista(operatorDataBody));
    }

    Promise.all(promises)
      .then((responses) => {
        let success = true;
        for (const res of responses) {
          if (res.data.Result !== 1) {
            toast.update(id, {
              render:
                t("OperatorsNotCreated") +
                (res?.data?.Exception ? ": " + res?.data?.Exception : ""),
              type: "error",
              autoClose: 3000,
              isLoading: false,
            });
            success = false;
            break;
          }
        }
        if (success) {
          toast.update(id, {
            render: t("OperatorsCreated"),
            type: "success",
            autoClose: 1500,
            isLoading: false,
          });
        }
      })
      .catch((error) => {
        console.error("Error during API calls:", error);
        toast.update(id, {
          render: t("OperatorsNotCreated"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      })
      .finally(() => {
        setMultiUploadInProgress(false);
        // setUploading(false);
        setUploadData({
          isUploaded: false, // Aggiunto qui per ripristinare il Dropzone
        });
      });
  };

  useEffect(() => {
    if (uploadSuccess) {
      toast.success(t("UploadComplete"));
    }
  }, [uploadSuccess, t]);

  const handleChange = () => {
    setIsAdmin(!isAdmin);
  };

  const handleChangeRadio = (event) => {
    setSendEmail(event.target.value === "true");
  };

  return (
    <div className="">
      <h3 className={"font-bold"}>{t("MultipleUpload")}</h3>
      <p>{t("DownloadTemplate")}</p>
      <div className="space-y-4 ">
        <CoreSelect
          name="ruolo"
          label={t("Role")}
          items={ruoli.map((el) => el)}
          value={selectedRuole}
          required={true}
          disabled
        />
        {role === ROLE.ADMIN && (
          <StudioFilter
            selectedUser={studio}
            setSelectedUser={(value) => {
              setStudio(value);
              setValidationErrors((prevErrors) => ({
                ...prevErrors,
                studio: "",
              }));
            }}
            usersList={studios?.Data?.items}
            setDebouncedInputUserValue={setDebouncedInputStudioValue}
            isError={!!validationErrors.studio}
          />
        )}
        {selectedRuole === t("Operator") && subRole !== SUB_ROLE.OPERATOR && (
          <div className="flex gap-5">
            <FormControlLabel
              control={<Switch checked={isAdmin} onChange={handleChange} />}
              label="ADMIN?"
            />
          </div>
        )}
      </div>
      {selectedRuole === t("User") ? (
        <>
          {studio && (
            <FormControl fullWidth size="small">
              {isLoading || isFetching ? (
                <CoreSpinner style={{ color: `${colorPrimary}` }} />
              ) : (
                <div className="mt-4 ">
                  <CoreSelectObjArr
                    required
                    name={`selectedCommercialista`}
                    label={`${t("SelectOperator")}`}
                    value={selectedCommercialista}
                    onChange={onChangeCommercialista}
                    items={commercialistiOptions || []}
                    error={!!validationErrors.selectedCommercialista}
                  />
                </div>
              )}
            </FormControl>
          )}
          {!uploadData?.isUploaded ? (
            <div className="mt-4">
              {selectedRuole === t("User") && (
                <CoreButtonHoverIcon
                  className={"m-1"}
                  href="/templates/create_user.xlsx"
                  download={true}
                  testo={`Template ${t("User")}`}
                  icon={<DownloadRoundedIcon />}
                />
              )}
              <div className="mt-4">
                <Dropzone
                  onDrop={handleDrop}
                  accept={{
                    ".xlsx": [
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      "application/vnd.ms-excel",
                    ],
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      style={{
                        padding: "20px",
                        border: "2px dashed #ccc",
                        textAlign: "center",
                        cursor: "pointer",
                      }}
                    >
                      <input {...getInputProps()} />
                      <Box display="flex" flexDirection="column">
                        <CoreButtonIcon
                          icon={
                            <UploadFileRoundedIcon
                              className="text-gray"
                              sx={{ fontSize: 70 }}
                            />
                          }
                        />
                        <CoreButtonIcon
                          icon={<SearchRoundedIcon className="text-gray" />}
                          testo={t("Search")}
                          className="text-gray"
                        />
                      </Box>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center pb-2">
                <h3 className="m-0">{"Inviare email di registrazione?"}</h3>

                <RadioGroup
                  row
                  value={sendEmail}
                  className="flex"
                  onChange={handleChangeRadio}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio size="small" />}
                    label={t("Yes")}
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio size="small" />}
                    label={t("No")}
                  />
                </RadioGroup>
              </div>
              <CreateUsers
                uploadData={uploadData}
                onReset={handleReset}
                onUpload={handleUpload}
                studio={studio}
                isCreating={multiUploadInProgress}
              />
            </>
          )}
        </>
      ) : (
        <>
          {!uploadData?.isUploaded ? (
            <div className="mt-4">
              {selectedRuole === t("Operator") && (
                <CoreButtonHoverIcon
                  className={"m-1"}
                  href="/templates/create_operator.xlsx"
                  download={true}
                  testo={`Template ${t("Operator")}`}
                  icon={<DownloadRoundedIcon />}
                />
              )}
              <div className="mt-4">
                <Dropzone
                  onDrop={handleDrop}
                  accept={{
                    ".xlsx": [
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      "application/vnd.ms-excel",
                    ],
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      style={{
                        padding: "20px",
                        border: "2px dashed #ccc",
                        textAlign: "center",
                        cursor: "pointer",
                      }}
                    >
                      <input {...getInputProps()} />
                      <Box display="flex" flexDirection="column">
                        <CoreButtonIcon
                          icon={
                            <UploadFileRoundedIcon
                              className="text-gray"
                              sx={{ fontSize: 70 }}
                            />
                          }
                        />
                        <CoreButtonIcon
                          icon={<SearchRoundedIcon className="text-gray" />}
                          testo={t("Search")}
                          className="text-gray"
                        />
                      </Box>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          ) : (
            <CreateOperator
              uploadData={uploadData}
              onReset={handleReset}
              onUpload={handleUploadOperator}
              isCreating={multiUploadInProgress}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ExcelFileUpload;
